import { initContract } from "@ts-rest/core";
import { z } from "zod";

import {
  ClientSchema,
  ProcessStepSchema,
  ProductLineSchema,
  QuoteRequestLineSchema,
  QuoteRequestSchema,
  QuoteRequestStatusSchema,
  QuoteSchema,
  UserSchema,
  TransportCalculationMethodSchema,
  ExtendedPartSchema,
  FileSchema,
} from "../types";

const c = initContract();

export const quoteRequestsContract = c.router({
  findOne: {
    method: "GET",
    path: "/quote-requests/:id",
    responses: {
      200: QuoteRequestSchema.extend({
        quoteRequestLines: QuoteRequestLineSchema.pick({
          id: true,
          shippingDelay: true,
          partId: true,
        }).array(),
        client: ClientSchema.nullable(),
      }),
    },
  },

  create: {
    method: "POST",
    path: "/quote-requests",
    body: z.optional(z.object({})),
    responses: {
      201: QuoteRequestSchema.merge(
        z.object({
          isFavorite: z.boolean().nullable(),
        }),
      ),
    },
    summary: "Create a quote request without client",
  },

  updateStatus: {
    method: "PATCH",
    path: "/quote-requests/:id/status",
    responses: {
      200: QuoteRequestSchema.merge(
        z.object({
          isFavorite: z.boolean().nullable(),
        }),
      ),
    },
    body: z.object({
      status: QuoteRequestStatusSchema,
    }),
    summary: "Update a quote request status",
  },

  update: {
    method: "PATCH",
    path: "/quote-requests/:id",
    body: QuoteRequestSchema.omit({
      createdById: true,
      shippingAddressId: true,
      companyId: true,
      ownerId: true,
    })
      .extend({
        expirationDate: z.string(),
        dueDate: z.string(),
        quoteRequestLines: QuoteRequestLineSchema.pick({
          id: true,
          shippingDelay: true,
        })
          .extend({ shippingDelay: z.string().nullish() })
          .array()
          .optional(),
      })
      .partial(),
    responses: {
      200: QuoteRequestSchema,
    },
    summary: "Update a quote request",
  },

  updateReference: {
    method: "PATCH",
    path: "/quote-requests/:id/reference",
    responses: {
      200: QuoteRequestSchema.merge(
        z.object({
          isFavorite: z.boolean().nullable(),
        }),
      ),
    },
    body: z.object({
      reference: z.string(),
    }),
    summary: "Update the quote request reference",
  },

  updateClient: {
    method: "PATCH",
    path: "/quote-requests/:id/client",
    responses: {
      200: QuoteRequestSchema.merge(
        z.object({
          isFavorite: z.boolean().nullable(),
        }),
      ),
    },
    body: z.object({
      clientId: z.string(),
    }),
    summary: "Update the client associated to a quote request",
  },

  updateArchived: {
    method: "PATCH",
    path: "/quote-requests/:id/archived",
    responses: {
      200: QuoteRequestSchema.merge(
        z.object({
          isFavorite: z.boolean().nullable(),
        }),
      ),
    },
    body: z.object({
      archived: z.boolean(),
    }),
    summary: "Update the archived status of a quote request",
  },

  updateQuoteRequestLinesReliability: {
    method: "PATCH",
    path: "/quote-requests/:id/quote-request-lines-reliability",
    body: z.object({
      status: z.boolean(),
    }),
    responses: {
      200: QuoteRequestSchema,
    },
    summary: "Update quote request lines reliability of a quoteRequest ",
  },

  findAll: {
    method: "GET",
    path: "/quote-requests",
    query: z.object({
      page: z.number().nonnegative().optional().default(1),
      status: QuoteRequestStatusSchema.optional(),
      ownerId: z.string().optional(),
      isFavorite: z.boolean().optional(),
    }),
    responses: {
      200: QuoteRequestSchema.merge(
        z.object({
          createdAt: z.string(),
          updatedAt: z.string(),
          dueDate: z.string().nullable(),
          quoteSentAt: z.string().nullable(),
          expirationDate: z.string().nullable(),
          quoteRequestLinesCount: z.number(),
          client: ClientSchema.nullable(),
          lastQuote: QuoteSchema.merge(
            z.object({
              createdAt: z.string(),
              updatedAt: z.string(),
              expirationDate: z.string().nullable(),
            }),
          ).nullable(),
          owner: UserSchema.nullable(),
          quoteRequestLines: QuoteRequestLineSchema.merge(
            z.object({
              reworkRatio: z.number().nullable(),
              part: ExtendedPartSchema.merge(
                z.object({
                  files: FileSchema.array().nullish(),
                }),
              ),
            }),
          ).array(),
        }),
      ).array(),
    },
    summary: "Find all quote requests",
  },
  findAllArchived: {
    method: "GET",
    path: "/quote-requests/archived",
    query: z.object({
      page: z.number().nonnegative().optional().default(1),
    }),
    responses: {
      200: QuoteRequestSchema.merge(
        z.object({
          isFavorite: z.boolean().nullable(),
          isArchived: z.boolean(),
          quoteRequestLines: QuoteRequestLineSchema.merge(
            z.object({
              reworkRatio: z.number().nullable(),
              part: ExtendedPartSchema.merge(
                z.object({
                  files: FileSchema.array().nullish(),
                }),
              ),
            }),
          ).array(),
        }),
      ).array(),
    },
    summary: "Find all archived quote requests",
  },
  findAllQuotes: {
    method: "GET",
    path: "/quote-requests/:id/quotes",
    responses: {
      200: QuoteSchema.array(),
    },
  },
  findProcessSteps: {
    method: "GET",
    path: "/quote-requests/:id/process-steps",
    responses: {
      200: ProcessStepSchema.extend({
        productLines: ProductLineSchema.array(),
      }).array(),
    },
    summary: "Get process steps for a quote request",
  },
  getWeight: {
    method: "GET",
    path: "/quote-requests/:id/weight",
    responses: {
      200: z.object({
        weight: z.number(),
      }),
    },
    summary: "Get the total weight of a quote request",
  },
  getTransport: {
    method: "GET",
    path: "/quote-requests/:id/transport",
    responses: {
      200: z.object({
        hasTransport: z.boolean(),
        totalCost: z.number(),
      }),
    },
    summary: "Get the total transport cost of a quote request",
  },
  updateTransport: {
    method: "PATCH",
    path: "/quote-requests/:id/transport",
    body: z.object({
      transportCost: z.number(),
      method: TransportCalculationMethodSchema,
      shouldDistributeCost: z.boolean(),
      distributionMethod: z.enum(["weight"]),
      isOutsourced: z.boolean().optional(),
    }),
    responses: {
      200: z.object({
        hasTransport: z.boolean(),
        affectedQuoteRequestLineIds: z.array(z.string()),
      }),
    },
    summary: "Update the total transport cost of a quote request",
  },
  exportMaterialConsultationCsv: {
    method: "GET",
    path: "/quote-requests/:id/exports/material-consultation",
    responses: {
      200: c.otherResponse({
        contentType: "text/csv",
        body: c.type<Buffer>(),
      }),
      204: z.void(),
    },
    summary: "Export the material formats consultation CSV for a quote request",
  },

  bulkCopy: {
    method: "POST",
    path: "/quote-requests/bulk-copy",
    body: z.object({
      sourceCompanyIds: z.array(z.string().uuid()),
      targetCompanyId: z.string().uuid(),
      limit: z.number().optional(),
    }),
    responses: {
      200: z.object({
        s3Copies: z.array(
          z.object({
            sourceKey: z.string(),
            targetKey: z.string(),
          }),
        ),
      }),
    },
  },
});
